// PDF processing function endpoint from .env config
const FUNCTION_URL = import.meta.env.VITE_APP_PDF_PROCESSING_BASE_URL;

export class FunctionService {
  /**
   * Builds the GET URL of the fileviewer for a token. Used as `<video src>` so the browser can
   * stream the file with HTTP Range requests (206 Partial Content) instead of downloading it whole.
   * Only valid for tokens short enough for a URL (FileWithToken.usePost === false).
   *
   * @param token - Secure file access token
   * @returns Absolute URL of `/api/fileviewer/{token}`
   */
  getFileStreamUrl(token: string): string {
    return `${FUNCTION_URL}/api/fileviewer/${encodeURIComponent(token)}`;
  }

  /**
   * Fetches a file blob from the PDF processing API endpoint, supporting streaming, progress reporting,
   * and optional abort signals.
   *
   * @param token - Secure file access token
   * @param fileSize - (Optional) Original file size as a fallback for progress calculation
   * @param onProgress - (Optional) Progress callback invoked as bytes are received
   * @param signal - (Optional) AbortSignal to cancel the fetch request
   * @returns Resolves to the resulting file Blob
   * @throws Error if the network request fails (or response has no readable stream)
   */
  async getFileBlob(
    token: string,
    fileSize?: number,
    onProgress?: (p: { percent?: number; receivedBytes: number; totalBytes?: number }) => void,
    signal?: AbortSignal,
  ): Promise<Blob> {
    // Initiate POST request to the PDF service endpoint
    const res = await fetch(`${FUNCTION_URL}/api/fileviewer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: token,
      signal,
    });

    // Ensure the network request succeeded and response body is streamable
    if (!res.ok || !res.body) {
      throw new Error(`Download failed (${res.status})`);
    }

    // Extract content type (for Blob creation), and content length for progress, if available
    const contentType = res.headers.get('Content-Type') || 'application/octet-stream';
    const contentLength = res.headers.get('Content-Length');
    // If the response doesn't specify a length, try using the known fileSize fallback
    const total = contentLength ? Number(contentLength) : fileSize;

    // Set up the response body as a readable stream
    const reader = res.body.getReader();
    let received = 0;

    // Create a new ReadableStream that tracks download progress and enqueues received chunks
    const stream = new ReadableStream<Uint8Array>({
      async pull(controller) {
        // Read a chunk from the response
        const { done, value } = await reader.read();
        if (done) {
          controller.close();
          return;
        }
        if (value) {
          received += value.byteLength;
          // Report progress if a callback is passed
          if (onProgress) {
            onProgress({
              receivedBytes: received,
              totalBytes: total,
              percent: total ? Math.round((received / total) * 100) : undefined,
            });
          }
          controller.enqueue(value);
        }
      },
      cancel(reason) {
        // Cancel the reader if the stream is aborted
        reader.cancel(reason as any);
      },
    });

    // Convert the entire received stream to a Blob with the correct content type
    const blob = await new Response(stream, { headers: { 'Content-Type': contentType } }).blob();
    return blob;
  }
}
